import React, { useRef, useLayoutEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import * as api from '../services/api';

export default function ImpersonationBanner() {
  const { user } = useAuth();
  const bannerRef = useRef<HTMLDivElement>(null);

  // Publish the banner's *rendered* height as a CSS custom property so that
  // fixed / sticky top-anchored surfaces (the full-screen editor, the
  // dashboard / checkout headers) can offset below it instead of being
  // overlapped by it. The height is measured (not hardcoded) because the
  // message wraps to 2 lines with long emails on narrow viewports (Bug 22.05
  // P2 #1). Default is 0px (var removed) whenever no one is impersonating, so
  // non-impersonating sessions are completely unaffected.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!user?.impersonatedBy) {
      root.style.removeProperty('--sf-impersonation-banner-h');
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const publish = () => root.style.setProperty('--sf-impersonation-banner-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--sf-impersonation-banner-h');
    };
  }, [user?.impersonatedBy]);

  if (!user?.impersonatedBy) return null;

  function handleReturn() {
    const origAccess = sessionStorage.getItem('sf_original_accessToken');
    const origRefresh = sessionStorage.getItem('sf_original_refreshToken');
    if (!origAccess || !origRefresh) {
      alert('Original admin session not found. Please sign in again.');
      api.setTokens(null);
      window.location.href = '/login';
      return;
    }
    api.setTokens({ accessToken: origAccess, refreshToken: origRefresh });
    sessionStorage.removeItem('sf_original_accessToken');
    sessionStorage.removeItem('sf_original_refreshToken');
    sessionStorage.removeItem('sf_impersonation_target_email');
    window.location.href = '/admin/clients';
  }

  return (
    <div
      ref={bannerRef}
      style={{
        // Bug 22.05 P2 #1: width:100% + boxSizing:border-box + flexWrap +
        // overflowWrap stop the banner from pushing the page wider than
        // the viewport on mobile when the impersonation message is long
        // (e.g. "You're viewing as long-user@longdomain.example.com
        // (impersonated by admin@creative813.com)"). Previously the sticky
        // banner sat outside ManagePage's overflow-x-clip wrapper and
        // forced body-level horizontal scroll, which visually read as
        // the Basics tab content "clipping past the right edge".
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        background: '#F7941D',
        color: '#1a1a1a',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        rowGap: '6px',
        flexWrap: 'wrap',
        fontSize: '13px',
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <span style={{ minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word', textAlign: 'center' }}>
        You're viewing as <strong>{user.email}</strong>
        {user.impersonatedByEmail ? ` (impersonated by ${user.impersonatedByEmail})` : ''}
      </span>
      <button
        onClick={handleReturn}
        style={{
          background: '#1a1a1a',
          color: '#fff',
          border: 'none',
          padding: '4px 12px',
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 700,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        Return to Admin
      </button>
    </div>
  );
}
